import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import TopBar, { iconBtn } from '../components/ui/TopBar';
import TagTicker from '../components/ui/TagTicker';
import SectionHeader from '../components/ui/SectionHeader';
import HorizontalCard from '../components/ui/HorizontalCard';
import VideoGrid from '../components/ui/VideoGrid';
import EditorRow from '../components/ui/EditorRow';
import LatestCommentCard from '../components/ui/LatestCommentCard';
import Icon from '../components/ui/Icon';
import { API } from '../utils/api';
import { getHistory } from '../utils/storage';

export default function HomePage() {
    const navigate = useNavigate();

    const [tags,     setTags]     = useState([]);
    const [hot,      setHot]      = useState([]);
    const [fresh,    setFresh]    = useState([]);
    const [picks,    setPicks]    = useState([]);
    const [comments, setComments] = useState([]);
    const [, setHistory]  = useState([]);
    const [contVideos, setContVideos] = useState([]);
    const [loading,  setLoading]  = useState(true);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [tagsRes, hotRes, freshRes, picksRes, commentsRes] = await Promise.all([
                fetch(`${API}/tags.php`),
                fetch(`${API}/trending.php?sort=rating&per_page=4`),
                fetch(`${API}/trending.php?per_page=8`),
                fetch(`${API}/trending.php?sort=monthly&per_page=6`),
                fetch(`${API}/latest_comments.php?per_page=6`),
            ]);

            const [tagsData, hotData, freshData, picksData, commentsData] = await Promise.all([
                tagsRes.json(),
                hotRes.json(),
                freshRes.json(),
                picksRes.json(),
                commentsRes.json(),
            ]);

            setTags(Array.isArray(tagsData) ? tagsData : []);
            setHot(hotData.results  ?? []);
            setFresh(freshData.results ?? []);
            setPicks(picksData.results ?? []);
            setComments(commentsData.results ?? []);
        } catch {}
        setLoading(false);
    }, []);

    // Load continue-watching from localStorage
    useEffect(() => {
        const h = getHistory().filter(item => item.progress > 0 && item.progress < 1);
        setHistory(h);

        if (h.length > 0) {
            const ids = h.slice(0, 6).map(item => item.videoId).join(',');
            fetch(`${API}/favorites.php?ids=${ids}&per_page=6`)
                .then(r => r.json())
                .then(data => {
                    const videos = data.results ?? data ?? [];
                    // Merge with progress from history
                    const merged = h.slice(0, 6).map(hi => {
                        const v = videos.find(vv => String(vv.id) === String(hi.videoId));
                        return v ? { ...v, _progress: hi.progress } : null;
                    }).filter(Boolean);
                    setContVideos(merged);
                })
                .catch(() => {});
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    return (
        <>
            <Helmet>
                <title>HentaiBaza — Лучший хентай в HD</title>
                <meta name="description" content="Крупнейшая коллекция хентай-аниме в HD качестве. Ежедневные обновления." />
            </Helmet>

            <div className="hb-screen">
                {/* Header */}
                <TopBar
                    left={
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                            <span style={{ fontSize: 18, fontWeight: 800, color: '#e60073', letterSpacing: -0.5 }}>
                                Hentai<span style={{ color: '#fff' }}>Baza</span>
                            </span>
                        </div>
                    }
                    right={
                        <>
                            <button onClick={() => navigate('/search')} style={iconBtn}>
                                <Icon name="search" size={20} color="#E4E4E7" />
                            </button>
                        </>
                    }
                />

                {/* Tag ticker */}
                <TagTicker tags={tags} />

                {loading ? (
                    <div className="hb-spinner" style={{ marginTop: 60 }} />
                ) : (
                    <>
                        {/* Continue watching */}
                        {contVideos.length > 0 && (
                            <section style={{ paddingTop: 22, paddingBottom: 6 }}>
                                <SectionHeader
                                    kicker="Продолжить"
                                    title="Где вы остановились"
                                    action={() => navigate('/profile?tab=history')}
                                    actionLabel="История"
                                />
                                <div
                                    className="hb-hscroll"
                                    style={{ display: 'flex', gap: 12, padding: '0 16px', overflowX: 'auto', scrollSnapType: 'x mandatory' }}
                                >
                                    {contVideos.map(v => (
                                        <div key={v.id} style={{ scrollSnapAlign: 'start' }}>
                                            <HorizontalCard video={v} progress={v._progress} width={200} />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* New releases */}
                        {fresh.length > 0 && (
                            <section style={{ paddingTop: 28 }}>
                                <SectionHeader
                                    kicker="На этой неделе"
                                    title="Новые релизы"
                                    action={() => navigate('/videos')}
                                />
                                <VideoGrid videos={fresh} columns={2} />
                            </section>
                        )}

                        {/* Trending */}
                        {hot.length > 0 && (
                            <section style={{ paddingTop: 28 }}>
                                <SectionHeader
                                    kicker="В тренде сегодня"
                                    title="Самое горячее"
                                    action={() => navigate('/videos?sort=rating')}
                                />
                                <VideoGrid videos={hot} columns={2} />
                            </section>
                        )}

                        {/* Editor's picks */}
                        {picks.length > 0 && (
                            <section style={{ paddingTop: 32 }}>
                                <SectionHeader kicker="За последний месяц" title="Самое популярное" />
                                <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {picks.map((v, i) => (
                                        <EditorRow key={v.id} video={v} rank={i + 1} />
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Последние комментарии */}
                        {comments.length > 0 && (
                            <section style={{ paddingTop: 32 }}>
                                <SectionHeader
                                    kicker="Живое обсуждение"
                                    title="Последние комментарии"
                                    action={() => navigate('/comments')}
                                    actionLabel="Все"
                                />
                                <div className="hb-comment-feed">
                                    {comments.map(c => (
                                        <LatestCommentCard key={c.id} comment={c} compact />
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Footer */}
                        <footer style={{
                            padding: '28px 16px 24px',
                            borderTop: '1px solid #131316',
                            marginTop: 28,
                            color: '#52525B', fontSize: 11,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                                <span style={{ fontSize: 14, fontWeight: 800, color: '#e60073' }}>
                                    Hentai<span style={{ color: '#71717A' }}>Baza</span>
                                </span>
                            </div>
                            <div style={{ lineHeight: 1.6, color: '#52525B', fontSize: 11 }}>
                                Крупнейшая коллекция хентай-аниме в HD. Ежедневные обновления.
                            </div>
                            <div style={{
                                marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap',
                                fontFamily: 'var(--mono)', fontSize: 10, color: '#3F3F46',
                            }}>
                                <span>2026 HENTAIBAZA</span>
                                <span>·</span>
                                <a href="/comments" style={{ color: '#52525B' }}>Комментарии</a>
                                <span>·</span>
                                <a href="/dmca" style={{ color: '#52525B' }}>DMCA</a>
                                <span>·</span>
                                <span>18 U.S.C. § 2257</span>
                            </div>
                        </footer>
                    </>
                )}
            </div>
        </>
    );
}
